import React from 'react';
import {
  IonInput,
  IonItem,
  IonLabel,
  IonTextarea,
  IonButton,
  IonIcon,
  IonChip,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
} from '@ionic/react';
import { create, close } from 'ionicons/icons';
import { 
  IssueFlaggerTable, 
  IssueFlaggerOutput
} from '../parameters-schema';
import DecisionTreeTable from '../shared/decision-tree-table';
import { 
  extractIssueFlaggerPaths, 
  padPathsToColumnCount 
} from '../shared/path-extraction';
import { 
  formatDecision, 
  getColumnDisplayName 
} from '../shared/decision-tree-utils';
import { useDecisionTreeEditor } from '../shared/use-decision-tree-editor';
import IssueFlaggerLeafEditor from './leaf-output-editor';
import { useTags } from '../../tags/tags-provider';
import { useParameters } from '../parameters-provider';
import ColumnSelector from '../shared/column-selector';

interface EnhancedInferenceRuleEditorProps {
  rule: IssueFlaggerTable;
  onUpdate: (newRule: IssueFlaggerTable) => void;
}

const EnhancedInferenceRuleEditor: React.FC<EnhancedInferenceRuleEditorProps> = ({ 
  rule, 
  onUpdate 
}) => {
  const { tags } = useTags();
  const { parameters } = useParameters();
  
  // Use shared decision tree editor hook
  const {
    isLeafEditorOpen,
    editingOutput,
    isColumnModalOpen,
    paths,
    handleEditOutput,
    handleSaveOutput,
    handleCreateSplit,
    handleEditSplit,
    handleDeleteBranch,
    handleAddColumn,
    handleDeleteColumn,
    handleAddNewColumn,
    closeLeafEditor,
    closeColumnModal
  } = useDecisionTreeEditor({
    table: rule,
    inputColumns: rule.inputColumns,
    decisionTree: rule.decisionTree,
    extractPaths: (tree) => {
      const extractedPaths = extractIssueFlaggerPaths(tree);
      return padPathsToColumnCount(extractedPaths, rule.inputColumns.length);
    },
    onUpdate
  });


  const handleUpdateTitle = (newTitle: string) => {
    onUpdate({
      ...rule,
      displayTitle: newTitle,
    });
  };

  const handleUpdateDescription = (newDescription: string) => {
    onUpdate({
      ...rule,
      parameterDescription: newDescription,
    });
  };

  const renderOutputCell = (output: IssueFlaggerOutput, rowIndex: number) => {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between',
        padding: '0.5rem',
        minHeight: '60px'
      }}>
        <div style={{ flex: 1, paddingRight: '0.5rem' }}>
          {/* Status */}
          <div style={{ 
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            <strong style={{ marginRight: '0.5rem' }}>Status:</strong> 
            <span style={{ 
              color: output.lightStatus === 'red' ? 'var(--ion-color-danger)' : 
                     output.lightStatus === 'yellow' ? 'var(--ion-color-warning)' : 
                     output.lightStatus === 'green' ? 'var(--ion-color-success)' :
                     'var(--ion-color-medium)',
              fontWeight: 'bold',
              fontSize: '0.9em'
            }}>
              {output.lightStatus || 'none'}
            </span>
          </div>
          
          {/* Explanation */}
          {output.explanation && (
            <div style={{ marginBottom: '0.75rem' }}>
              <strong style={{ display: 'block', marginBottom: '0.25rem' }}>Explanation:</strong>
              <div style={{ 
                fontSize: '0.85em', 
                color: 'var(--ion-color-medium)',
                lineHeight: '1.4',
                padding: '0.25rem 0'
              }}>
                {output.explanation.length > 100 
                  ? `${output.explanation.substring(0, 100)}...` 
                  : output.explanation
                }
              </div>
            </div>
          )}
          
          {/* Tags */}
          {output.tags && output.tags.length > 0 && (
            <div>
              <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Issue Tags:</strong>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '0.5rem',
                alignItems: 'center'
              }}>
                {output.tags.slice(0, 3).map((tagId: string, index: number) => {
                  const tag = tags?.find(t => t.id === tagId);
                  return (
                    <IonChip key={index} color="secondary" style={{ margin: 0 }}>
                      <IonLabel style={{ fontSize: '0.8em' }}>
                        {tag?.displayName || `Tag ${index + 1}`}
                      </IonLabel>
                    </IonChip>
                  );
                })}
                {output.tags.length > 3 && (
                  <IonChip color="medium" style={{ margin: 0 }}>
                    <IonLabel style={{ fontSize: '0.8em' }}>
                      +{output.tags.length - 3} more
                    </IonLabel>
                  </IonChip>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Edit Button */}
        <IonButton
          fill="clear"
          size="small"
          style={{ 
            alignSelf: 'flex-start',
            marginTop: '0.25rem',
            minWidth: '32px',
            height: '32px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleEditOutput(rowIndex, output);
          }}
        >
          <IonIcon icon={create} />
        </IonButton>
      </div>
    );
  };

  return (
    <div className="enhanced-inference-rule-editor">
      {/* Title and Description - Direct fields on parent */}
      <IonItem>
        <IonLabel position="stacked">Title</IonLabel>
        <IonInput
          value={rule.displayTitle}
          onIonChange={e => handleUpdateTitle(e.detail.value || '')}
          placeholder="Enter a title for this inference rule"
        />
      </IonItem>
      
      <IonItem>
        <IonLabel position="stacked">Description</IonLabel>
        <IonTextarea
          value={rule.parameterDescription}
          onIonChange={e => handleUpdateDescription(e.detail.value || '')}
          placeholder="Describe what this inference rule checks for"
        />
      </IonItem>

      <DecisionTreeTable
        table={rule}
        onUpdate={onUpdate}
        getColumnDisplayName={(column) => getColumnDisplayName(column, tags || [], parameters || [])}
        formatDecision={formatDecision}
        renderOutputCell={renderOutputCell}
        onEditOutput={handleEditOutput}
        onCreateSplit={handleCreateSplit}
        onEditSplit={handleEditSplit}
        onDeleteBranch={handleDeleteBranch}
        onAddColumn={handleAddColumn}
        onDeleteColumn={handleDeleteColumn}
        paths={paths}
      />

      <IssueFlaggerLeafEditor
        output={editingOutput || { lightStatus: 'green', explanation: '', tags: [] }}
        onUpdate={handleSaveOutput}
        isOpen={isLeafEditorOpen}
        onClose={closeLeafEditor}
      />

      {/* Column Selection Modal */}
      <IonModal isOpen={isColumnModalOpen} onDidDismiss={closeColumnModal}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Add Column</IonTitle>
            <IonButton
              fill="clear"
              slot="end"
              onClick={closeColumnModal}
            >
              <IonIcon icon={close} />
            </IonButton>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <ColumnSelector
            onAddColumn={handleAddNewColumn}
            existingColumns={rule.inputColumns}
            showTagsAndLightStatus={false}
          />
        </IonContent>
      </IonModal>

      <style>{`
        .enhanced-inference-rule-editor {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default EnhancedInferenceRuleEditor;
